import { ExternalLink, BookOpen, Link2, Linkedin, Youtube, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { prefetchHandlers } from '@/lib/prefetch';

type Topic = 'local-seo' | 'youtube-seo' | 'ecommerce-seo' | 'meta-ads' | 'digital-marketing';

interface Resource {
  label: string;
  url: string;
  source: string;
}

const externalByTopic: Record<Topic, Resource[]> = {
  'local-seo': [
    { label: 'Google Business Profile Help', url: 'https://support.google.com/business', source: 'Google' },
    { label: 'Local Search Ranking Factors', url: 'https://moz.com/learn/seo/local-ranking-factors', source: 'Moz' },
    { label: 'Local SEO Guide 2025', url: 'https://www.searchenginejournal.com/local-seo/guide/', source: 'Search Engine Journal' },
    { label: 'Local SEO Strategy', url: 'https://ahrefs.com/blog/local-seo/', source: 'Ahrefs' },
  ],
  'youtube-seo': [
    { label: 'YouTube Creator Academy', url: 'https://creatoracademy.youtube.com/', source: 'YouTube' },
    { label: 'YouTube SEO Guide', url: 'https://backlinko.com/youtube-seo', source: 'Backlinko' },
    { label: 'Video SEO Best Practices', url: 'https://moz.com/learn/seo/video', source: 'Moz' },
    { label: 'YouTube Algorithm Explained', url: 'https://www.searchenginejournal.com/youtube-seo/', source: 'SEJ' },
  ],
  'ecommerce-seo': [
    { label: 'E-commerce SEO Guide', url: 'https://ahrefs.com/blog/ecommerce-seo/', source: 'Ahrefs' },
    { label: 'Shopify SEO Checklist', url: 'https://www.shopify.com/blog/ecommerce-seo-beginners-guide', source: 'Shopify' },
    { label: 'Product Schema Guide', url: 'https://developers.google.com/search/docs/appearance/structured-data/product', source: 'Google' },
    { label: 'E-commerce Ranking Factors', url: 'https://moz.com/learn/seo/ecommerce', source: 'Moz' },
  ],
  'meta-ads': [
    { label: 'Meta Ads Manager Help', url: 'https://www.facebook.com/business/help', source: 'Meta' },
    { label: 'Meta Pixel Setup Guide', url: 'https://developers.facebook.com/docs/meta-pixel', source: 'Meta for Developers' },
    { label: 'Facebook Ads Benchmarks', url: 'https://www.wordstream.com/blog/ws/2017/02/28/facebook-advertising-benchmarks', source: 'WordStream' },
    { label: 'Conversions API Guide', url: 'https://www.facebook.com/business/help/2041148702652965', source: 'Meta Business' },
  ],
  'digital-marketing': [
    { label: 'Think with Google Insights', url: 'https://www.thinkwithgoogle.com/', source: 'Google' },
    { label: 'HubSpot Marketing Blog', url: 'https://blog.hubspot.com/marketing', source: 'HubSpot' },
    { label: 'Content Marketing Institute', url: 'https://contentmarketinginstitute.com/', source: 'CMI' },
    { label: 'Digital Marketing Stats', url: 'https://www.semrush.com/blog/digital-marketing-statistics/', source: 'Semrush' },
  ],
};

const internalByTopic: Record<Topic, { label: string; href: string }[]> = {
  'local-seo': [
    { label: 'Local SEO Case Study', href: '/projects/local-seo' },
    { label: 'Technical SEO Project', href: '/projects/technical-seo' },
    { label: 'YouTube SEO Service', href: '/services/youtube-seo' },
    { label: 'E-commerce SEO Service', href: '/services/ecommerce-seo' },
    { label: 'Read our SEO Blog', href: '/blog' },
    { label: 'View Full Portfolio', href: '/portfolio' },
  ],
  'youtube-seo': [
    { label: 'YouTube SEO Case Study', href: '/projects/youtube-seo' },
    { label: 'Content SEO Project', href: '/projects/content-seo' },
    { label: 'Local SEO Service', href: '/services/local-seo' },
    { label: 'Digital Marketing Service', href: '/services/digital-marketing' },
    { label: 'Read our SEO Blog', href: '/blog' },
    { label: 'View Full Portfolio', href: '/portfolio' },
  ],
  'ecommerce-seo': [
    { label: 'E-commerce Case Study', href: '/projects/ecommerce-seo' },
    { label: 'Technical SEO Project', href: '/projects/technical-seo' },
    { label: 'Meta Ads Service', href: '/services/meta-ads' },
    { label: 'Local SEO Service', href: '/services/local-seo' },
    { label: 'Read our SEO Blog', href: '/blog' },
    { label: 'View Full Portfolio', href: '/portfolio' },
  ],
  'meta-ads': [
    { label: 'Meta Ads Case Study (Zinzo)', href: '/projects/meta-ads-zinzo' },
    { label: 'E-commerce SEO Project', href: '/projects/ecommerce-seo' },
    { label: 'Digital Marketing Service', href: '/services/digital-marketing' },
    { label: 'YouTube SEO Service', href: '/services/youtube-seo' },
    { label: 'Read our Marketing Blog', href: '/blog' },
    { label: 'View Full Portfolio', href: '/portfolio' },
  ],
  'digital-marketing': [
    { label: 'Meta Ads Case Study', href: '/projects/meta-ads-zinzo' },
    { label: 'SEO Analytics Project', href: '/projects/seo-analytics' },
    { label: 'Local SEO Service', href: '/services/local-seo' },
    { label: 'E-commerce SEO Service', href: '/services/ecommerce-seo' },
    { label: 'Read our Marketing Blog', href: '/blog' },
    { label: 'View Full Portfolio', href: '/portfolio' },
  ],
};

const profiles = [
  { label: 'LinkedIn Profile', url: 'https://www.linkedin.com/in/rakibhossentopu/', Icon: Linkedin },
  { label: 'YouTube Channel', url: 'https://www.youtube.com/@rakibhossentopu', Icon: Youtube },
  { label: 'Facebook Page', url: 'https://www.facebook.com/rakibhossentopu', Icon: Facebook },
];

interface Props {
  topic: Topic;
  heading?: string;
}

const AuthorityResources = ({ topic, heading = 'Helpful Resources & Further Reading' }: Props) => {
  const externals = externalByTopic[topic];
  const internals = internalByTopic[topic];

  return (
    <section className="py-20 bg-background border-t border-border/50">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-primary font-medium text-sm uppercase tracking-widest">Resources</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-12">{heading}</h2>

          <div className="grid md:grid-cols-2 gap-10">
            {/* Internal links */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Link2 className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Explore More on Our Site</h3>
              </div>
              <ul className="space-y-3">
                {internals.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      {...prefetchHandlers(item.href)}
                      className="group flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/40 group-hover:bg-primary transition-colors" />
                      <span className="border-b border-transparent group-hover:border-primary/50">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* External authority links */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Trusted Industry Sources</h3>
              </div>
              <ul className="space-y-3">
                {externals.map((r) => (
                  <li key={r.url}>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start gap-2 text-foreground/80 hover:text-primary transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 mt-1 flex-shrink-0 text-primary/60 group-hover:text-primary" />
                      <span>
                        <span className="border-b border-transparent group-hover:border-primary/50">{r.label}</span>
                        <span className="text-muted-foreground text-sm ml-2">— {r.source}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-border/50">
                <p className="text-sm text-muted-foreground mb-4">Connect with Rakib Hossen Topu</p>
                <div className="flex gap-3">
                  {profiles.map(({ label, url, Icon }) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="w-10 h-10 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthorityResources;
